import React, { useState, useEffect } from 'react';
import { X, FileText, Clock, AlertCircle, CheckCircle, XCircle, RefreshCw, Download, Search, Folder, Settings } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CloudConnection, CloudFile, SyncLog } from '../../services/api';
import { apiService } from '../../services/api';
import Button from './Button';

interface CloudConnectionConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  connection: CloudConnection | null;
}

interface FolderItem {
  id: string;
  name: string;
  path: string;
  parents: string[];
  modifiedTime?: string;
}

const CloudConnectionConfigModal: React.FC<CloudConnectionConfigModalProps> = ({
  isOpen,
  onClose,
  connection
}) => {
  const [activeTab, setActiveTab] = useState<'details' | 'files' | 'history' | 'logs' | 'folders'>('details');
  const [files, setFiles] = useState<CloudFile[]>([]);
  const [logs, setLogs] = useState<SyncLog[]>([]);
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [selectedFolders, setSelectedFolders] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingFolders, setSavingFolders] = useState(false);
  
  // États pour le chargement lazy par onglet
  const [filesLoaded, setFilesLoaded] = useState(false);
  const [logsLoaded, setLogsLoaded] = useState(false);
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [loadingFolders, setLoadingFolders] = useState(false);

  useEffect(() => {
    if (isOpen && connection) {
      console.log('🔄 Modal ouverte, réinitialisation des états', {
        connectionId: connection.id,
        lastSync: connection.last_sync
      });
      
      // Réinitialiser les états quand la modal s'ouvre
      setFolders([]);
      setSelectedFolders([]);
      setFiles([]); // Réinitialiser aussi les fichiers pour forcer le rechargement
      setFilesLoaded(false);
      setLogsLoaded(false);
      setFoldersLoaded(false);
      
      // Charger uniquement les dossiers sélectionnés depuis la connexion (pas besoin d'appel API)
      const syncFolders = connection.sync_folders || [];
      setSelectedFolders(syncFolders);
    } else if (!isOpen) {
      // Quand la modale se ferme, réinitialiser les états pour le prochain chargement
      console.log('🔒 Modal fermée, réinitialisation complète');
      setFiles([]);
      setFilesLoaded(false);
      setLogsLoaded(false);
      setFoldersLoaded(false);
    }
  }, [isOpen, connection?.id]);

  // Charger les fichiers quand l'onglet "Détails" ou "Fichiers" est ouvert (pour les statistiques)
  useEffect(() => {
    if (isOpen && connection && (activeTab === 'details' || activeTab === 'files') && !filesLoaded && !loadingFiles) {
      console.log('🔄 useEffect: Déclenchement du chargement des fichiers', {
        isOpen,
        connectionId: connection?.id,
        activeTab,
        filesLoaded,
        loadingFiles
      });
      loadFiles();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, connection?.id, activeTab]);

  // Charger les logs seulement quand l'onglet "Historique" ou "Logs" est ouvert
  useEffect(() => {
    if (isOpen && connection && (activeTab === 'history' || activeTab === 'logs') && !logsLoaded && !loadingLogs) {
      loadLogs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, connection?.id, activeTab]);

  // Charger les dossiers seulement quand l'onglet "Dossiers" est ouvert
  useEffect(() => {
    if (isOpen && connection && activeTab === 'folders' && !foldersLoaded && !loadingFolders) {
      console.log('🔄 useEffect: Déclenchement du chargement des dossiers', {
        isOpen,
        connectionId: connection?.id,
        activeTab,
        foldersLoaded,
        loadingFolders
      });
      loadFolders();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, connection?.id, activeTab]);

  const loadFiles = async () => {
    if (!connection) {
      console.warn('⚠️ loadFiles: Pas de connexion');
      return;
    }
    
    if (filesLoaded) {
      console.log('✅ loadFiles: Fichiers déjà chargés, skip');
      return;
    }
    
    console.log(`📥 Chargement des fichiers pour la connexion ${connection.id}...`);
    setLoadingFiles(true);
    try {
      const filesData = await apiService.getCloudFiles(connection.id);
      console.log('📦 Données fichiers reçues:', filesData);
      console.log('📦 Type:', typeof filesData);
      console.log('📦 Est un tableau?', Array.isArray(filesData));
      console.log('📦 Nombre de fichiers:', Array.isArray(filesData) ? filesData.length : 'N/A');
      
      const filesArray = Array.isArray(filesData) ? filesData : [];
      setFiles(filesArray);
      setFilesLoaded(true);
      console.log(`✅ ${filesArray.length} fichier(s) chargé(s) avec succès`);
    } catch (err) {
      console.error('❌ Erreur lors du chargement des fichiers:', err);
      console.error('❌ Détails:', err);
      setFiles([]);
      setFilesLoaded(true);
    } finally {
      setLoadingFiles(false);
    }
  };

  const loadLogs = async () => {
    if (!connection || logsLoaded) return;
    
    setLoadingLogs(true);
    try {
      const logsData = await apiService.getSyncLogs(connection.id);
      setLogs(Array.isArray(logsData) ? logsData : []);
      setLogsLoaded(true);
    } catch (err) {
      console.warn('Erreur lors du chargement des logs:', err);
      setLogs([]);
      setLogsLoaded(true);
    } finally {
      setLoadingLogs(false);
    }
  };

  const loadFolders = async () => {
    if (!connection) {
      console.warn('⚠️ loadFolders: Pas de connexion');
      return;
    }
    
    if (foldersLoaded) {
      console.log('✅ loadFolders: Dossiers déjà chargés, skip');
      return;
    }
    
    const isGoogleDrive = connection.provider_detail?.name === 'google-drive' || 
                         (typeof connection.provider === 'object' && connection.provider !== null && 'name' in connection.provider && connection.provider.name === 'google-drive');
    
    console.log('🔍 Vérification Google Drive pour chargement dossiers:', {
      provider_detail_name: connection.provider_detail?.name,
      provider_type: typeof connection.provider,
      provider_name: typeof connection.provider === 'object' ? connection.provider?.name : connection.provider,
      isGoogleDrive
    });
    
    if (!isGoogleDrive) {
      console.log('⚠️ Ce n\'est pas Google Drive, pas de chargement de dossiers');
      setFoldersLoaded(true);
      return;
    }
    
    console.log(`📁 Démarrage du chargement des dossiers pour la connexion ${connection.id}...`);
    setLoadingFolders(true);
    try {
      const foldersData = await apiService.getCloudFolders(connection.id, 3);
      console.log('📁 Données dossiers reçues:', foldersData);
      console.log('📁 Type de données:', typeof foldersData);
      console.log('📁 Est un tableau?', Array.isArray(foldersData));
      
      // Gérer différents formats de réponse
      let foldersList: FolderItem[] = [];
      if (Array.isArray(foldersData)) {
        foldersList = foldersData;
        console.log('📁 Format: Tableau direct');
      } else if (foldersData?.folders && Array.isArray(foldersData.folders)) {
        foldersList = foldersData.folders;
        console.log('📁 Format: Objet avec propriété folders');
      } else if (foldersData && typeof foldersData === 'object' && 'results' in foldersData && Array.isArray((foldersData as any).results)) {
        foldersList = (foldersData as any).results;
        console.log('📁 Format: Objet avec propriété results');
      } else {
        console.warn('⚠️ Format de réponse inattendu:', Object.keys(foldersData || {}));
        foldersList = [];
      }
      
      console.log(`📁 ${foldersList.length} dossier(s) extrait(s) de la réponse`);
      if (foldersList.length > 0) {
        console.log('📁 Exemples de dossiers:', foldersList.slice(0, 3).map(f => ({ id: f.id, name: f.name })));
      }
      
      setFolders(foldersList);
      setFoldersLoaded(true);
      console.log('✅ Dossiers chargés avec succès');
    } catch (err: any) {
      console.error('❌ Erreur lors du chargement des dossiers:', err);
      console.error('❌ Détails de l\'erreur:', {
        message: err?.message,
        response: err?.response?.data,
        status: err?.response?.status
      });
      setFolders([]);
      setFoldersLoaded(true);
    } finally {
      setLoadingFolders(false);
    }
  };

  // Fonction pour recharger les données (utilisée par les boutons "Actualiser")
  const loadData = async () => {
    if (activeTab === 'files') {
      setFilesLoaded(false);
      await loadFiles();
    } else if (activeTab === 'history' || activeTab === 'logs') {
      setLogsLoaded(false);
      await loadLogs();
    } else if (activeTab === 'folders') {
      setFoldersLoaded(false);
      await loadFolders();
    }
  };

  const handleSyncFolders = async () => {
    if (!connection) return;

    // Vérifier qu'au moins un dossier est sélectionné
    if (selectedFolders.length === 0) {
      const confirmSyncAll = window.confirm(
        '⚠️ Aucun dossier sélectionné\n\n' +
        'Si vous synchronisez sans sélectionner de dossier, TOUS les dossiers de votre Google Drive seront synchronisés.\n\n' +
        'Cela peut prendre beaucoup de temps si vous avez beaucoup de fichiers.\n\n' +
        'Voulez-vous continuer ?'
      );
      if (!confirmSyncAll) {
        return;
      }
    }

    setSavingFolders(true);
    try {
      // Sauvegarder la configuration des dossiers sélectionnés
      await apiService.updateSyncFolders(connection.id, selectedFolders);
      
      const folderCount = selectedFolders.length;
      const folderText = folderCount === 0 
        ? 'tous les dossiers'
        : `${folderCount} dossier(s) sélectionné(s)`;
      
      // Lancer directement la synchronisation
      console.log(`🔄 Démarrage de la synchronisation pour ${folderText}...`);
      
      // Afficher un message informatif que la synchronisation peut prendre du temps
      const syncMessage = window.confirm(
        `🔄 Synchronisation en cours...\n\n` +
        `📁 ${folderText} seront synchronisés.\n\n` +
        `⏱️ Cela peut prendre plusieurs minutes selon le nombre de fichiers.\n\n` +
        `Voulez-vous continuer ?`
      );
      
      if (!syncMessage) {
        setSavingFolders(false);
        return;
      }
      
      try {
        // Afficher un message que la synchronisation est en cours
        alert('🔄 Synchronisation démarrée !\n\n⏱️ Cela peut prendre plusieurs minutes. Vous pouvez suivre la progression dans l\'onglet "Fichiers" ou "Logs".');
        
        const syncResult = await apiService.syncCloudFiles(connection.id);
        
        if (syncResult.success) {
          const syncedCount = syncResult.result?.synced_files || syncResult.result?.synced_count || 0;
          const indexedCount = syncResult.result?.indexed_files || 0;
          
          let message = `✅ Synchronisation terminée !\n\n`;
          message += `📁 ${folderText} synchronisé(s)\n`;
          message += `📄 ${syncedCount} fichier(s) téléchargé(s)`;
          if (indexedCount > 0) {
            message += `\n🔍 ${indexedCount} fichier(s) indexé(s) avec RAG`;
          }
          
          alert(message);
          
          // Recharger les fichiers pour mettre à jour les statistiques
          setFilesLoaded(false);
          await loadFiles();
        } else {
          const errorMsg = syncResult.result?.error || syncResult.result?.message || 'Erreur inconnue';
          alert(`❌ Erreur lors de la synchronisation:\n\n${errorMsg}`);
        }
      } catch (syncError: any) {
        console.error('Erreur lors de la synchronisation:', syncError);
        
        // Gérer spécifiquement les erreurs de timeout réseau (mais pas de timeout côté client)
        let errorMessage = syncError?.response?.data?.error || syncError?.message || 'Erreur inconnue';
        
        if (errorMessage.includes('timeout') || errorMessage.includes('aborted') || errorMessage.includes('trop de temps')) {
          errorMessage = 'La synchronisation prend plus de temps que prévu. Elle continue en arrière-plan. Vérifiez l\'onglet "Fichiers" ou "Logs" pour voir la progression.';
        }
        
        alert(`❌ Erreur lors de la synchronisation:\n\n${errorMessage}`);
      }
    } catch (error: any) {
      console.error('Erreur lors de la sauvegarde des dossiers:', error);
      alert('❌ Erreur lors de la sauvegarde de la configuration des dossiers');
    } finally {
      setSavingFolders(false);
    }
  };

  const toggleFolder = (folderId: string) => {
    setSelectedFolders(prev => 
      prev.includes(folderId) 
        ? prev.filter(id => id !== folderId)
        : [...prev, folderId]
    );
  };

  if (!isOpen || !connection) return null;

  const providerName = connection.provider_detail?.display_name || 
                      (typeof connection.provider === 'object' ? connection.provider.display_name : 'Cloud Provider');

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Jamais';
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'failed':
      case 'error':
        return <XCircle className="w-4 h-4 text-red-600" />;
      case 'syncing':
      case 'running':
        return <RefreshCw className="w-4 h-4 text-blue-600 animate-spin" />;
      default:
        return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getLogLevelColor = (level: string) => {
    switch (level.toLowerCase()) {
      case 'success':
        return 'text-green-600 bg-green-50';
      case 'error':
        return 'text-red-600 bg-red-50';
      case 'warning':
        return 'text-yellow-600 bg-yellow-50';
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  // Détecter si c'est Google Drive avec plusieurs méthodes
  const isGoogleDrive = connection && (
    connection.provider_detail?.name === 'google-drive' || 
    (typeof connection.provider === 'object' && connection.provider !== null && 'name' in connection.provider && connection.provider.name === 'google-drive') ||
    (typeof connection.provider === 'number' && connection.provider_detail?.name === 'google-drive')
  );
  
  console.log('🔍 Détection Google Drive dans tabs:', {
    hasConnection: !!connection,
    provider_detail_name: connection?.provider_detail?.name,
    provider_type: typeof connection?.provider,
    provider_value: connection?.provider,
    isGoogleDrive
  });

  const tabs = [
    { id: 'details', label: 'Détails', icon: <FileText className="w-4 h-4" /> },
    ...(isGoogleDrive ? [{ id: 'folders', label: 'Dossiers', icon: <Folder className="w-4 h-4" /> }] : []),
    { id: 'files', label: 'Fichiers', icon: <Download className="w-4 h-4" /> },
    { id: 'history', label: 'Historique', icon: <Clock className="w-4 h-4" /> },
    { id: 'logs', label: 'Logs', icon: <AlertCircle className="w-4 h-4" /> }
  ];

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[100] overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0, scale: 0.9, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9, y: 20 }}
              transition={{ duration: 0.2 }}
              className="bg-white rounded-lg shadow-xl max-w-4xl w-full border-2 border-blue-200 my-8 relative z-[101]"
            >
              {/* Header */}
              <div className="p-6 border-b">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">
                        Configuration - {providerName}
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        {connection.account_email}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={onClose}
                    className="text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="border-b bg-gray-50">
                <div className="flex space-x-1 px-6">
                  {tabs.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id as any)}
                      className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium transition-colors border-b-2 ${
                        activeTab === tab.id
                          ? 'border-blue-600 text-blue-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700'
                      }`}
                    >
                      {tab.icon}
                      <span>{tab.label}</span>
                    </button>
                  ))}
                </div>
              </div>

                    {/* Content */}
                    <div className="p-6 max-h-[60vh] overflow-y-auto">
                      {(loadingFiles || loadingLogs || loadingFolders) && (
                        <div className="flex items-center justify-center py-12">
                          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
                          <span className="ml-3 text-gray-600">Chargement...</span>
                        </div>
                      )}
                      {!(loadingFiles || loadingLogs || loadingFolders) && (
                        <>
                    {/* Tab: Details */}
                    {activeTab === 'details' && (
                      <div className="space-y-6">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-3">Informations de connexion</h4>
                          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Service:</span>
                              <span className="text-sm font-medium text-gray-900">{providerName}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Compte:</span>
                              <span className="text-sm font-medium text-gray-900">{connection.account_email}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Nom:</span>
                              <span className="text-sm font-medium text-gray-900">{connection.account_name}</span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Statut:</span>
                              <span className={`text-sm font-medium ${connection.is_active ? 'text-green-600' : 'text-red-600'}`}>
                                {connection.is_active ? 'Actif' : 'Inactif'}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Dernière synchronisation:</span>
                              <span className="text-sm font-medium text-gray-900">
                                {formatDate(connection.last_sync)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Connexion créée:</span>
                              <span className="text-sm font-medium text-gray-900">
                                {formatDate(connection.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-semibold text-gray-900">Statistiques</h4>
                            <Button
                              size="sm"
                              variant="outline"
                              icon={<RefreshCw className={`w-4 h-4 ${loadingFiles ? 'animate-spin' : ''}`} />}
                              onClick={() => {
                                setFilesLoaded(false);
                                loadFiles();
                              }}
                              disabled={loadingFiles}
                            >
                              {loadingFiles ? 'Chargement...' : 'Actualiser'}
                            </Button>
                          </div>
                          {loadingFiles ? (
                            <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                              <div className="flex items-center space-x-3">
                                <RefreshCw className="w-5 h-5 text-blue-600 animate-spin" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-blue-800">
                                    Chargement des statistiques...
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : files.length === 0 ? (
                            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <div className="flex items-start space-x-3">
                                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                                <div className="flex-1">
                                  <p className="text-sm font-medium text-yellow-800">
                                    Aucun fichier synchronisé
                                  </p>
                                  <p className="text-sm text-yellow-700 mt-1">
                                    Cliquez sur le bouton "Synchroniser" dans la page d'intégrations pour télécharger vos fichiers depuis {providerName}.
                                  </p>
                                </div>
                              </div>
                            </div>
                          ) : null}
                          <div className="grid grid-cols-3 gap-4">
                            <div className="bg-blue-50 rounded-lg p-4">
                              <div className="text-2xl font-bold text-blue-600">
                                {loadingFiles ? '...' : files.length}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">Fichiers synchronisés</div>
                            </div>
                            <div className="bg-green-50 rounded-lg p-4">
                              <div className="text-2xl font-bold text-green-600">
                                {loadingFiles ? '...' : files.filter(f => f.is_synced).length}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">Fichiers indexés</div>
                            </div>
                            <div className="bg-purple-50 rounded-lg p-4">
                              <div className="text-2xl font-bold text-purple-600">
                                {loadingFiles ? '...' : formatFileSize(files.reduce((sum, f) => sum + (f.file_size || 0), 0))}
                              </div>
                              <div className="text-sm text-gray-600 mt-1">Taille totale</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Tab: Folders */}
                    {activeTab === 'folders' && (
                      <div className="space-y-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <h4 className="text-sm font-semibold text-gray-900">
                              Sélectionner les dossiers à synchroniser
                            </h4>
                            <p className="text-xs text-gray-500 mt-1">
                              {selectedFolders.length === 0 
                                ? '⚠️ Aucun dossier sélectionné - Tous les dossiers seront synchronisés (peut être lent)'
                                : `✅ ${selectedFolders.length} dossier(s) sélectionné(s) - Seuls ces dossiers seront synchronisés`}
                            </p>
                          </div>
                          <div className="flex space-x-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedFolders([])}
                              disabled={selectedFolders.length === 0 || savingFolders}
                            >
                              Tout désélectionner
                            </Button>
                            <Button
                              size="sm"
                              icon={<RefreshCw className={`w-4 h-4 ${savingFolders ? 'animate-spin' : ''}`} />}
                              onClick={handleSyncFolders}
                              disabled={savingFolders}
                            >
                              {savingFolders ? 'Synchronisation...' : 'Synchroniser'}
                            </Button>
                          </div>
                        </div>

                        {loadingFolders ? (
                          <div className="text-center py-12">
                            <RefreshCw className="w-8 h-8 mx-auto mb-3 text-blue-600 animate-spin" />
                            <p className="text-gray-900 font-medium mb-2">Chargement des dossiers...</p>
                            <p className="text-sm text-gray-600 mb-2">
                              Récupération de la structure de votre Google Drive...
                            </p>
                            <p className="text-xs text-gray-500 italic">
                              ⏱️ Cela peut prendre quelques instants si vous avez beaucoup de dossiers. Veuillez patienter...
                            </p>
                          </div>
                        ) : folders.length === 0 ? (
                          <div className="text-center py-12">
                            <Folder className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-gray-900 font-medium mb-2">Aucun dossier trouvé</p>
                            <p className="text-sm text-gray-600">
                              Les dossiers de votre Google Drive seront listés ici.
                            </p>
                          </div>
                        ) : (
                          <>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                              <p className="text-sm text-blue-800">
                                <strong>💡 Astuce :</strong> Sélectionnez les dossiers que vous souhaitez synchroniser. 
                                Si aucun dossier n'est sélectionné, tous les dossiers seront synchronisés (peut être lent).
                              </p>
                            </div>
                            <div className="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
                              <div className="divide-y divide-gray-200">
                                {folders.map((folder) => {
                                  const isSelected = selectedFolders.includes(folder.id);
                                  return (
                                    <div
                                      key={folder.id}
                                      className={`p-3 hover:bg-gray-50 cursor-pointer transition-colors ${
                                        isSelected ? 'bg-blue-50 border-l-4 border-blue-600' : ''
                                      }`}
                                      onClick={() => toggleFolder(folder.id)}
                                    >
                                      <div className="flex items-center space-x-3">
                                        <div className={`flex-shrink-0 w-5 h-5 rounded border-2 flex items-center justify-center ${
                                          isSelected 
                                            ? 'bg-blue-600 border-blue-600' 
                                            : 'border-gray-300'
                                        }`}>
                                          {isSelected && (
                                            <CheckCircle className="w-4 h-4 text-white" />
                                          )}
                                        </div>
                                        <Folder className={`w-5 h-5 flex-shrink-0 ${
                                          isSelected ? 'text-blue-600' : 'text-gray-400'
                                        }`} />
                                        <div className="flex-1 min-w-0">
                                          <p className={`text-sm font-medium ${
                                            isSelected ? 'text-blue-900' : 'text-gray-900'
                                          }`}>
                                            {folder.name}
                                          </p>
                                          <p className="text-xs text-gray-500 truncate">
                                            {folder.path}
                                          </p>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          </>
                        )}
                      </div>
                    )}

                    {/* Tab: Files */}
                    {activeTab === 'files' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Fichiers synchronisés ({files.length})
                          </h4>
                          <Button
                            size="sm"
                            variant="outline"
                            icon={<RefreshCw className={`w-4 h-4 ${loadingFiles ? 'animate-spin' : ''}`} />}
                            onClick={loadData}
                            disabled={loadingFiles}
                          >
                            {loadingFiles ? 'Chargement...' : 'Actualiser'}
                          </Button>
                        </div>
                        {loadingFiles ? (
                          <div className="text-center py-12">
                            <RefreshCw className="w-8 h-8 mx-auto mb-3 text-blue-600 animate-spin" />
                            <p className="text-gray-900 font-medium mb-2">Chargement des fichiers...</p>
                          </div>
                        ) : files.length === 0 ? (
                          <div className="text-center py-12">
                            <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p className="text-gray-900 font-medium mb-2">Aucun fichier synchronisé</p>
                            <p className="text-sm text-gray-600 mb-4">
                              Lancez une synchronisation depuis la page d'intégrations pour télécharger vos fichiers depuis {providerName}.
                            </p>
                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto">
                              <p className="text-sm text-blue-800">
                                <strong>💡 Astuce :</strong> Cliquez sur le bouton <strong>"Synchroniser"</strong> à côté de {providerName} dans la page d'intégrations.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {files.map((file) => (
                              <div
                                key={file.id}
                                className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                              >
                                <div className="flex items-center space-x-3 flex-1 min-w-0">
                                  {getStatusIcon(file.sync_status)}
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 truncate">{file.name}</div>
                                    <div className="text-sm text-gray-500">
                                      {formatFileSize(file.file_size || 0)} • {file.file_type}
                                    </div>
                                  </div>
                                </div>
                                <div className="flex items-center space-x-2">
                                  {file.is_synced && (
                                    <span className="px-2 py-1 text-xs font-medium bg-green-100 text-green-700 rounded">
                                      Indexé
                                    </span>
                                  )}
                                  <span className={`px-2 py-1 text-xs font-medium rounded ${
                                    file.sync_status === 'completed' ? 'bg-green-100 text-green-700' :
                                    file.sync_status === 'failed' ? 'bg-red-100 text-red-700' :
                                    'bg-yellow-100 text-yellow-700'
                                  }`}>
                                    {file.sync_status}
                                  </span>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tab: History */}
                    {activeTab === 'history' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Historique des synchronisations ({logs.filter(log => log.level !== 'debug').length})
                          </h4>
                          <Button
                            size="sm"
                            variant="outline"
                            icon={<RefreshCw className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} />}
                            onClick={loadData}
                            disabled={loadingLogs}
                          >
                            {loadingLogs ? 'Chargement...' : 'Actualiser'}
                          </Button>
                        </div>
                        {loadingLogs ? (
                          <div className="text-center py-12">
                            <RefreshCw className="w-8 h-8 mx-auto mb-3 text-blue-600 animate-spin" />
                            <p className="text-gray-900 font-medium mb-2">Chargement de l'historique...</p>
                          </div>
                        ) : logs.length === 0 ? (
                          <div className="text-center py-12 text-gray-500">
                            <Clock className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>Aucun historique disponible</p>
                          </div>
                        ) : (
                          <div className="space-y-3">
                            {logs.slice(0, 20).map((log, index) => (
                              <div
                                key={index}
                                className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg"
                              >
                                <div className={`p-1 rounded ${getLogLevelColor(log.level)}`}>
                                  {getStatusIcon(log.level === 'success' ? 'completed' : log.level === 'error' ? 'failed' : 'pending')}
                                </div>
                                <div className="flex-1 min-w-0">
                                  <div className="text-sm font-medium text-gray-900">{log.message}</div>
                                  <div className="text-xs text-gray-500 mt-1">
                                    {formatDate(log.timestamp)}
                                  </div>
                                  {log.details && typeof log.details === 'object' && (
                                    <div className="text-xs text-gray-600 mt-2 bg-white p-2 rounded">
                                      {JSON.stringify(log.details, null, 2)}
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Tab: Logs */}
                    {activeTab === 'logs' && (
                      <div>
                        <div className="flex items-center justify-between mb-4">
                          <h4 className="text-sm font-semibold text-gray-900">
                            Logs détaillés ({logs.length})
                          </h4>
                          <Button
                            size="sm"
                            variant="outline"
                            icon={<RefreshCw className={`w-4 h-4 ${loadingLogs ? 'animate-spin' : ''}`} />}
                            onClick={loadData}
                            disabled={loadingLogs}
                          >
                            {loadingLogs ? 'Chargement...' : 'Actualiser'}
                          </Button>
                        </div>
                        {loadingLogs ? (
                          <div className="text-center py-12">
                            <RefreshCw className="w-8 h-8 mx-auto mb-3 text-blue-600 animate-spin" />
                            <p className="text-gray-900 font-medium mb-2">Chargement des logs...</p>
                          </div>
                        ) : logs.length === 0 ? (
                          <div className="text-center py-12 text-gray-500">
                            <AlertCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                            <p>Aucun log disponible</p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {logs.map((log, index) => (
                              <div
                                key={index}
                                className={`p-3 rounded-lg border-l-4 ${
                                  log.level === 'error' ? 'bg-red-50 border-red-500' :
                                  log.level === 'success' ? 'bg-green-50 border-green-500' :
                                  log.level === 'warning' ? 'bg-yellow-50 border-yellow-500' :
                                  'bg-blue-50 border-blue-500'
                                }`}
                              >
                                <div className="flex items-start justify-between">
                                  <div className="flex-1">
                                    <div className="flex items-center space-x-2 mb-1">
                                      <span className={`text-xs font-medium px-2 py-1 rounded ${
                                        getLogLevelColor(log.level)
                                      }`}>
                                        {log.level.toUpperCase()}
                                      </span>
                                      <span className="text-xs text-gray-500">
                                        {formatDate(log.timestamp)}
                                      </span>
                                    </div>
                                    <div className="text-sm text-gray-900">{log.message}</div>
                                    {log.details && (
                                      <details className="mt-2">
                                        <summary className="text-xs text-gray-600 cursor-pointer hover:text-gray-900">
                                          Détails
                                        </summary>
                                        <pre className="mt-2 text-xs bg-white p-2 rounded overflow-x-auto">
                                          {JSON.stringify(log.details, null, 2)}
                                        </pre>
                                      </details>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer */}
              <div className="p-6 border-t bg-gray-50 flex items-center justify-end space-x-3">
                <Button variant="outline" onClick={onClose}>
                  Fermer
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default CloudConnectionConfigModal;

